import { supabase } from '../../../shared/lib/supabase';
import type { Job, Result } from '../../../shared/lib/supabase';

const getCurrentUserId = async (): Promise<string> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  return user.id;
};

export interface JobWithResult extends Job {
  result?: Result | null;
}

// List all jobs for the current user
export const listJobs = async (): Promise<JobWithResult[]> => {
  const userId = await getCurrentUserId();
  
  const { data: jobs, error: jobsError } = await supabase
    .from('jobs')
    .select(`
      *,
      results (*)
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (jobsError) {
    console.error('Error fetching jobs:', jobsError);
    throw new Error(`Failed to fetch jobs: ${jobsError.message}`);
  }

  // Transform to match your current structure
  return jobs.map(job => ({
    ...job,
    _id: job.id, // Add _id for compatibility with existing code
    createdAt: new Date(job.created_at).getTime(), // Convert to timestamp for compatibility
    result: job.results?.[0] || null
  })) as JobWithResult[];
};

// List all Odyssey jobs for the current user
export const listOdysseyJobs = async () => {
  const userId = await getCurrentUserId();
  
  const { data: odysseyJobs, error: odysseyError } = await supabase
    .from('odyssey_jobs')
    .select('job_id, name, status, created_at, project_id')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(500);

  if (odysseyError) {
    console.error('Error fetching Odyssey jobs:', odysseyError);
    throw new Error(`Failed to fetch Odyssey jobs: ${odysseyError.message}`);
  }

  return odysseyJobs || [];
};

// Lightweight aggregate stats for the welcome page (no heavy row fetches)
export const getOdysseyJobStats = async (): Promise<{ total: number; completed: number; running: number; thisWeek: number }> => {
  const userId = await getCurrentUserId();
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);

  const [totalRes, completedRes, runningRes, weekRes] = await Promise.all([
    supabase.from('odyssey_jobs').select('job_id', { count: 'exact', head: true }).eq('user_id', userId),
    supabase.from('odyssey_jobs').select('job_id', { count: 'exact', head: true }).eq('user_id', userId).eq('status', 'completed'),
    supabase.from('odyssey_jobs').select('job_id', { count: 'exact', head: true }).eq('user_id', userId).in('status', ['processing', 'pending']),
    supabase.from('odyssey_jobs').select('job_id', { count: 'exact', head: true }).eq('user_id', userId).gt('created_at', weekAgo.toISOString())
  ]);

  const firstError = totalRes.error || completedRes.error || runningRes.error || weekRes.error;
  if (firstError) {
    console.error('Error fetching Odyssey stats:', firstError);
    throw new Error(`Failed to fetch Odyssey stats: ${firstError.message}`);
  }

  return {
    total: totalRes.count || 0,
    completed: completedRes.count || 0,
    running: runningRes.count || 0,
    thisWeek: weekRes.count || 0
  };
};

// Get Odyssey jobs for a specific project
export const getOdysseyJobsForProject = async (projectId: string) => {
  // Remove user filter to allow team members to see each other's jobs
  // Database policies will handle access control
  
  // Fetch only the fields needed for the project overview and cap the result
  // This avoids Postgres statement timeouts from large full-table sorts
  const { data: odysseyJobs, error } = await supabase
    .from('odyssey_jobs')
    .select('job_id, name, base_job_id, status, created_at, project_id, num_gens')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
    .limit(100);

  if (error) {
    console.error('Error fetching Odyssey jobs for project:', error);
    throw new Error(`Failed to fetch Odyssey jobs: ${error.message}`);
  }

  return odysseyJobs || [];
};

// Get the latest Odyssey job for a project
export const getLatestOdysseyJobForProject = async (projectId: string) => {
  const userId = await getCurrentUserId();
  const { data, error } = await supabase
    .from('odyssey_jobs')
    .select('job_id, name, base_job_id, status, created_at, project_id')
    .eq('project_id', projectId)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching latest Odyssey job for project:', error);
    throw new Error(`Failed to fetch Odyssey jobs: ${error.message}`);
  }
  return data || null;
};

// Check if project has any Odyssey jobs
export const hasOdysseyJobInProject = async (projectId: string): Promise<boolean> => {
  // Remove user filter to allow team members to see each other's jobs
  // Database policies will handle access control
  const { count, error } = await supabase
    .from('odyssey_jobs')
    .select('job_id', { count: 'exact', head: true })
    .eq('project_id', projectId)
    .limit(1);
  if (error) {
    console.error('Error checking Odyssey job existence:', error);
    throw new Error(`Failed to fetch Odyssey jobs: ${error.message}`);
  }
  return (count || 0) > 0;
};

// Get count of completed Odyssey jobs for a project (this is the generation count)
export const getOdysseyGenerationCount = async (projectId: string): Promise<number> => {
  // Remove user filter to allow team members to see each other's jobs
  // Database policies will handle access control
  // Use a lightweight count-only query instead of fetching all rows
  const { count, error } = await supabase
    .from('odyssey_jobs')
    .select('job_id', { count: 'exact', head: true })
    .eq('project_id', projectId)
    .eq('status', 'completed');

  if (error) {
    console.error('Error counting Odyssey jobs for project:', error);
    throw new Error(`Failed to fetch Odyssey jobs: ${error.message}`);
  }

  return count || 0;
};

// Create a new job
export const createJob = async ({
  pdb_file_url,
  smiles,
  name,
  project_id = null,
  workflow_type
}: {
  pdb_file_url: string;
  smiles: string;
  name: string;
  project_id?: string | null;
  workflow_type?: 'enzyme' | 'antibody' | 'peptide';
}) => {
  const userId = await getCurrentUserId();
  
  // If project_id is provided but workflow_type is not, fetch the project's workflow type
  let finalWorkflowType = workflow_type;
  if (project_id && !workflow_type) {
    try {
      const { data: project } = await supabase
        .from('projects')
        .select('workflow_type')
        .eq('id', project_id)
        .single();
      
      if (project) {
        finalWorkflowType = project.workflow_type;
      }
    } catch (error) {
      console.error('Error fetching project workflow type:', error);
    }
  }
  
  console.log('🔍 Creating job with parameters:', {
    user_id: userId,
    pdb_file_url,
    smiles,
    name,
    project_id,
    workflow_type: finalWorkflowType,
    status: 'queued'
  });

  // Create the job
  const { data: job, error: jobError } = await supabase
    .from('jobs')
    .insert({
      user_id: userId,
      pdb_file_url,
      smiles,
      name,
      project_id,
      workflow_type: finalWorkflowType,
      status: 'queued',
      generation: 1
      // base_job_id will be set after creation
    })
    .select()
    .single();

  if (jobError) {
    console.error('Error creating job:', jobError);
    throw new Error(`Failed to create job: ${jobError.message}`);
  }
  
  console.log('🔍 Job created successfully:', job);

  // Update base_job_id to point to itself for first generation
  const { error: updateError } = await supabase
    .from('jobs')
    .update({ base_job_id: job.id })
    .eq('id', job.id);

  if (updateError) {
    console.error('Error updating base_job_id:', updateError);
    // Don't throw here, job was created successfully
  }

  // Create associated result record
  const { error: resultError } = await supabase
    .from('results')
    .insert({
      job_id: job.id
    });

  if (resultError) {
    console.error('Error creating result:', resultError);
    // Don't throw here, job was created successfully
  }

  console.log('🔍 Returning job ID:', job.id);
  return job.id;
};

// Delete a job and its results
export const deleteJob = async (jobId: string) => {
  const userId = await getCurrentUserId();

  // First verify the user owns this job
  const { data: job, error: fetchError } = await supabase
    .from('jobs')
    .select('user_id')
    .eq('id', jobId)
    .single();

  if (fetchError || !job) {
    throw new Error('Job not found');
  }

  if (job.user_id !== userId) {
    throw new Error('Job not found or unauthorized');
  }

  // Delete results first (due to foreign key constraint)
  const { error: resultsError } = await supabase
    .from('results')
    .delete()
    .eq('job_id', jobId);

  if (resultsError) {
    console.error('Error deleting results:', resultsError);
    // Continue anyway
  }

  // Delete the job
  const { error: jobError } = await supabase
    .from('jobs')
    .delete()
    .eq('id', jobId);

  if (jobError) {
    console.error('Error deleting job:', jobError);
    throw new Error(`Failed to delete job: ${jobError.message}`);
  }

  return { success: true };
};

// Get a single job with results
export const getJob = async (jobId: string): Promise<JobWithResult | null> => {
  const userId = await getCurrentUserId();

  const { data: job, error } = await supabase
    .from('jobs')
    .select(`
      *,
      results (*)
    `)
    .eq('id', jobId)
    .eq('user_id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // No rows returned
    }
    console.error('Error fetching job:', error);
    throw new Error(`Failed to fetch job: ${error.message}`);
  }

  return {
    ...job,
    _id: job.id,
    createdAt: new Date(job.created_at).getTime(),
    result: job.results?.[0] || null
  } as JobWithResult;
};

// Update a job (e.g., assign to project, update comments)
export const updateJob = async (jobId: string, updates: Partial<{
  name: string;
  project_id: string;
  status: string;
  comments: string;
}>) => {
  const userId = await getCurrentUserId();

  // First verify the user owns this job
  const { data: job, error: fetchError } = await supabase
    .from('jobs')
    .select('user_id')
    .eq('id', jobId)
    .single();

  if (fetchError || !job) {
    throw new Error('Job not found');
  }

  if (job.user_id !== userId) {
    throw new Error('Job not found or unauthorized');
  }

  // Update the job
  const { error: updateError } = await supabase
    .from('jobs')
    .update(updates)
    .eq('id', jobId);

  if (updateError) {
    console.error('Error updating job:', updateError);
    throw new Error(`Failed to update job: ${updateError.message}`);
  }

  return { success: true };
};

// Get the latest job for a project (highest generation)
export const getLatestJobForProject = async (projectId: string): Promise<JobWithResult | null> => {
  const userId = await getCurrentUserId();
  
  const { data: job, error } = await supabase
    .from('jobs')
    .select(`
      *,
      results (*)
    `)
    .eq('project_id', projectId)
    .eq('user_id', userId)
    .order('generation', { ascending: false })
    .limit(1)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // No job found
    }
    console.error('Error fetching latest job:', error);
    throw new Error(`Failed to fetch latest job: ${error.message}`);
  }

  return {
    ...job,
    _id: job.id,
    createdAt: new Date(job.created_at).getTime(),
    result: job.results?.[0] || null
  } as JobWithResult;
};

// Get the last N jobs for a project (sorted by generation)
export const getRecentJobsForProject = async (projectId: string, limit: number = 5): Promise<JobWithResult[]> => {
  const userId = await getCurrentUserId();
  
  const { data: jobs, error } = await supabase
    .from('jobs')
    .select(`
      *,
      results (*)
    `)
    .eq('project_id', projectId)
    .eq('user_id', userId)
    .order('generation', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching recent jobs:', error);
    throw new Error(`Failed to fetch recent jobs: ${error.message}`);
  }

  return jobs.map(job => ({
    ...job,
    _id: job.id,
    createdAt: new Date(job.created_at).getTime(),
    result: job.results?.[0] || null
  })) as JobWithResult[];
};

// Check if a project has any jobs
export const hasJobInProject = async (projectId: string): Promise<boolean> => {
  const userId = await getCurrentUserId();
  
  const { count, error } = await supabase
    .from('jobs')
    .select('*', { count: 'exact', head: true })
    .eq('project_id', projectId)
    .eq('user_id', userId);

  if (error) {
    console.error('Error checking for jobs:', error);
    return false;
  }

  return (count || 0) > 0;
};

// Get all generations for a job family
export const getJobGenerations = async (baseJobId: string): Promise<JobWithResult[]> => {
  const userId = await getCurrentUserId();
  
  const { data: jobs, error } = await supabase
    .from('jobs')
    .select(`
      *,
      results (*)
    `)
    .eq('base_job_id', baseJobId)
    .eq('user_id', userId)
    .order('generation', { ascending: true });

  if (error) {
    console.error('Error fetching job generations:', error);
    throw new Error(`Failed to fetch job generations: ${error.message}`);
  }

  return jobs.map(job => ({
    ...job,
    _id: job.id,
    createdAt: new Date(job.created_at).getTime(),
    result: job.results?.[0] || null
  })) as JobWithResult[];
};

// Get job result
export const getJobResult = async (jobId: string): Promise<Result | null> => {
  const { data: result, error } = await supabase
    .from('results')
    .select('*')
    .eq('job_id', jobId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // No result found
    }
    console.error('Error fetching job result:', error);
    throw new Error(`Failed to fetch job result: ${error.message}`);
  }

  return result;
};

// Create next generation job
export const createNextGeneration = async ({
  projectId,
  smiles,
  name
}: {
  projectId: string;
  smiles: string;
  name?: string;
}) => {
  const userId = await getCurrentUserId();
  
  // 1. Get the latest generation for this project
  const latestJob = await getLatestJobForProject(projectId);
  
  if (!latestJob) {
    throw new Error('No existing job found for this project. Please create an initial job first.');
  }
  
  // 2. Get the result from latest generation
  const result = await getJobResult(latestJob.id);
  
  if (!result?.rfdiffusion_pdb_url) {
    throw new Error('Previous generation has not completed yet or has no output PDB. Please wait for it to complete.');
  }
  
  // 3. Create new job with incremented generation
  const newGeneration = latestJob.generation + 1;
  
  // Get the base job name if no name is provided
  let jobName = name;
  if (!jobName && latestJob.base_job_id) {
    // If this is not the first generation, get the base job's name
    if (latestJob.generation === 1) {
      // latestJob is the base job
      jobName = latestJob.name;
    } else {
      // Need to fetch the base job to get its name
      const { data: baseJob } = await supabase
        .from('jobs')
        .select('name')
        .eq('id', latestJob.base_job_id)
        .single();
      
      if (baseJob) {
        jobName = baseJob.name;
      }
    }
  }
  
  // Fallback to Generation X if still no name
  jobName = jobName || `Generation ${newGeneration}`;
  
  console.log('🔍 Creating next generation job:', {
    project_id: projectId,
    generation: newGeneration,
    base_job_id: latestJob.base_job_id,
    previous_job_id: latestJob.id,
    job_name: jobName
  });

  // Create the job
  const { data: job, error: jobError } = await supabase
    .from('jobs')
    .insert({
      user_id: userId,
      pdb_file_url: result.rfdiffusion_pdb_url, // Use output from previous generation
      smiles,
      name: jobName,
      project_id: projectId,
      workflow_type: latestJob.workflow_type,
      status: 'queued',
      generation: newGeneration,
      base_job_id: latestJob.base_job_id
    })
    .select()
    .single();

  if (jobError) {
    console.error('Error creating next generation job:', jobError);
    throw new Error(`Failed to create next generation job: ${jobError.message}`);
  }
  
  console.log('🔍 Next generation job created successfully:', job);

  // Create associated result record
  const { error: resultError } = await supabase
    .from('results')
    .insert({
      job_id: job.id
    });

  if (resultError) {
    console.error('Error creating result for next generation:', resultError);
    // Don't throw here, job was created successfully
  }

  console.log('🔍 Returning next generation job ID:', job.id);
  return job.id;
};

// Month-based calendar functions for performance optimization
const getMonthRange = (year: number, month: number) => {
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);
  return {
    start: startDate.toISOString(),
    end: endDate.toISOString()
  };
};

// Get jobs for a specific month
export const getJobsForMonth = async (year: number, month: number) => {
  const userId = await getCurrentUserId();
  const { start, end } = getMonthRange(year, month);
  
  const { data: jobs, error } = await supabase
    .from('jobs')
    .select('id, name, created_at, status, workflow_type, project_id')
    .eq('user_id', userId)
    .gte('created_at', start)
    .lte('created_at', end)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching jobs for month:', error);
    throw new Error(`Failed to fetch jobs: ${error.message}`);
  }

  return jobs || [];
};

// Get Odyssey jobs for a specific month
export const getOdysseyJobsForMonth = async (year: number, month: number) => {
  const userId = await getCurrentUserId();
  const { start, end } = getMonthRange(year, month);
  
  const { data: odysseyJobs, error } = await supabase
    .from('odyssey_jobs')
    .select('job_id, name, created_at, status, project_id')
    .eq('user_id', userId)
    .gte('created_at', start)
    .lte('created_at', end)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching Odyssey jobs for month:', error);
    throw new Error(`Failed to fetch Odyssey jobs: ${error.message}`);
  }

  return odysseyJobs || [];
};
